import { keymap } from 'prosemirror-keymap';
import { Fragment, Node as ProsemirrorNode, NodeType, Schema, Slice } from 'prosemirror-model';
import { EditorState, Transaction, Plugin } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';

import { ColumnsKeysService } from './columns-keys.service';
import { ColumnsMenuController, ColumnValue } from './columns.plugins.types';
import { ColumnsService } from './columns.service';
import { ColumnsSchema } from './nodes/columns.node';
import {
  EditorButton,
  EditorMode,
  ProsemirrorCommand,
  ProsemirrorDispatch,
  RtPMPlugin,
} from '../../editor/types';
import { DEFAULT_KEYMAP_INPUTRULE_PRIORITY } from '../../helpers/utils';

class ColumnsEditorButton implements EditorButton {
  public readonly isDropdown = true;

  constructor(
    public editorView: EditorView,
    private readonly menuController: ColumnsMenuController,
    private readonly columnsService: ColumnsService,
  ) {}

  public get icon(): 'view-column-1' | 'view-column-2' | 'view-column-3' | 'view-column-4' {
    const active: ColumnValue = this.getActiveColumns();
    switch (active) {
      case 1:
        return 'view-column-1';
      case 2:
        return 'view-column-2';
      case 3:
        return 'view-column-3';
      case 4:
        return 'view-column-4';
      default:
        return 'view-column-3';
    }
  }

  public get tooltipText() {
    return this.menuController?.isOpen?.() ? '' : 'wysiwyg.columns.name';
  }

  public isActive() {
    return this.menuController?.isOpen?.() ?? false;
  }

  public get name() {
    return 'columns';
  }

  public run(state: EditorState, dispatch: (tr: Transaction) => void, origin: HTMLElement) {
    if (!this.editorView) return;

    const { backdropClick$ } = this.menuController.open(origin.getBoundingClientRect(), {
      getActive: () => this.getActiveColumns(),
      select: val => {
        this.columnsService.setColumns(val, state, this.editorView);
        this.menuController.close();
        this.editorView?.focus();
      },
    });

    backdropClick$.subscribe(() => {
      this.menuController.close();
      this.editorView?.focus();
    });

    this.editorView.focus();
  }

  private getActiveColumns(state: EditorState = this.editorView.state): ColumnValue {
    return <ColumnValue>this.columnsService.getColumnsCount(state);
  }
}

export class ColumnsPlugin implements RtPMPlugin {
  public buttons: { toggleColumns: EditorButton };
  public api: { columnsService: ColumnsService; columnsKeysService: ColumnsKeysService };
  private editorView: EditorView;
  private columnsNodeType: NodeType;

  constructor(private readonly menuController: ColumnsMenuController) {}

  public beforeInit(mode: EditorMode, schema: Schema) {
    const columnsNode = schema.nodes.columns;
    const columnNode = schema.nodes.column;
    const paragraphNode = schema.nodes.paragraph;
    const columnsService = new ColumnsService(columnsNode, columnNode, paragraphNode, schema);
    const columnsKeysService = new ColumnsKeysService(columnsService);
    this.columnsNodeType = columnsNode;
    this.api = { columnsService, columnsKeysService };
  }

  public afterInit(mode: EditorMode, editorView: EditorView) {
    this.editorView = editorView;
    this.buttons = {
      toggleColumns: new ColumnsEditorButton(
        editorView,
        this.menuController,
        this.api.columnsService,
      ),
    };
    (<ColumnsEditorButton>this.buttons.toggleColumns).editorView = editorView;
  }

  public getNativeEditorPlugins(schema: ColumnsSchema) {
    return [
      {
        priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY - 1,
        plugin: this.getPasteTransformPlugin(this.columnsNodeType),
      },
      { priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY, plugin: keymap(this.getKeyBindings()) },
    ];
  }

  private getKeyBindings(): Record<string, ProsemirrorCommand> {
    return {
      Enter: (state: EditorState, dispatch?: ProsemirrorDispatch) => {
        const safeDispatch = dispatch ? (tr: Transaction) => dispatch(tr) : null;

        if (this.api.columnsKeysService.handleEnter(state, safeDispatch)) {
          return true;
        }
        return false;
      },
      Backspace: (state: EditorState, dispatch?: ProsemirrorDispatch) => {
        const safeDispatch = dispatch ? (tr: Transaction) => dispatch(tr) : null;

        if (this.api.columnsKeysService.handleBackspace(state, safeDispatch, this.editorView)) {
          return true;
        }
        return false;
      },
    };
  }

  // fix copying from column and pasting into column
  private getPasteTransformPlugin(columnsNodeType: NodeType): Plugin {
    return new Plugin({
      props: {
        transformPasted(slice: Slice): Slice {
          let fragment = slice.content;
          const pastingInsideText = slice.openStart > 0;

          // --- CASE 1: Unwrap `columns` node if necessary
          if (fragment.childCount === 1 && fragment.firstChild?.type.name === 'columns') {
            const columnsNode = fragment.firstChild;
            const columnNodes: ProsemirrorNode[] = [];
            columnsNode.forEach(col => columnNodes.push(col));
            fragment = Fragment.fromArray(columnNodes);
          }

          // --- CASE 2: Pasting inside text => flatten everything!
          if (pastingInsideText) {
            const nodes: ProsemirrorNode[] = [];

            fragment.forEach(node => {
              if (node.type.name === 'column') {
                node.forEach(child => nodes.push(child)); // flatten column contents
              } else {
                nodes.push(node); // keep normal node
              }
            });

            return new Slice(Fragment.fromArray(nodes), 0, 0);
          }

          // --- CASE 3: Normal paste (outside text)
          // Check if everything is column
          const columnNodes: ProsemirrorNode[] = [];
          let nonIgnorableNodesFound = false;

          fragment.forEach(node => {
            if (node.type.name === 'column') {
              columnNodes.push(node);
            } else if (node.type.name === 'paragraph' && node.content.size === 0) {
              // empty paragraph: ignore
            } else {
              nonIgnorableNodesFound = true;
            }
          });

          if (!nonIgnorableNodesFound && columnNodes.length > 0) {
            if (columnNodes.length === 1) {
              const column = columnNodes[0];
              return new Slice(column.content, slice.openStart - 1, slice.openEnd - 1);
            } else {
              const newColumnsNode = columnsNodeType.create({}, columnNodes);
              return new Slice(Fragment.from(newColumnsNode), 0, 0);
            }
          }

          // --- CASE 4: mixed content outside text
          return slice;
        },
      },
    });
  }
}
